import {
	Component,
	forwardRef,
	Input,
	EventEmitter,
	Output,
} from '@angular/core';
import { CustomerLink, CustomerClient } from 'src/shared/api';
import {
	displayName,
	touchControl,
} from 'src/shared/utilities/utility-functions';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
	takeUntil,
	map,
	filter,
	distinctUntilChanged,
	skipWhile,
	debounceTime,
} from 'rxjs/operators';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { TypeAheadService } from 'src/shared/services/type-ahead.service';

@Component({
	selector: 'rpshared-customer-link',
	templateUrl: './customer-link.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CustomerLinkComponent),
			multi: true,
		},
	],
})
export class CustomerLinkComponent extends SelfDestruct
	implements ControlValueAccessor {
	private _required = false;
	@Input()
	get required(): boolean {
		return this._required;
	}
	set required(value: boolean) {
		this._required = coerceBooleanProperty(value);
	}

	private _readonly = false;
	@Input()
	get readonly(): boolean {
		return this._readonly;
	}
	set readonly(value: boolean) {
		this._readonly = coerceBooleanProperty(value);
	}

	@Output() optionSelected = new EventEmitter<CustomerLink>();

	initialValue: CustomerLink;
	customer = new FormControl('') as FormControl;

	filteredCustomers$ = this.typeAheadService.filteredCustomers$(
		this.customer.valueChanges
	);

	// Redeclared template variables
	displayName = displayName;

	constructor(
		private readonly typeAheadService: TypeAheadService,
		private readonly customerClient: CustomerClient
	) {
		super();
	}

	// model --> view
	writeValue(value: CustomerLink | number): void {
		if (typeof value === 'number') {
			this.customerClient.getCustomerLink(value).subscribe((customer) => {
				this.initialValue = customer;
				this.customer.setValue(customer, { emitEvent: false });
			});
		} else if (value) {
			this.initialValue = value;
			this.customer.setValue(value, { emitEvent: false });
		} else {
			this.customer.reset('', { emitEvent: false });
		}
	}

	// view --> model
	registerOnChange(fn: (value: CustomerLink | number) => void) {
		this.customer.valueChanges
			.pipe(
				debounceTime(200),
				filter((v) => !v || typeof v !== 'string'),
				distinctUntilChanged(),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}

	registerOnTouched(fn: () => void) {
		this.onTouched = touchControl(this.customer)(fn);
	}

	onTouched: () => void = () => {};
}

@Component({
	selector: 'rpshared-customer-id',
	templateUrl: './customer-link.component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => CustomerIdComponent),
			multi: true,
		},
	],
})
export class CustomerIdComponent extends CustomerLinkComponent {
	registerOnChange(fn: (value: CustomerLink | number) => void) {
		this.customer.valueChanges
			.pipe(
				filter((v) => !v || typeof v !== 'string'),
				map((v) => (v ? v.id : null)),
				distinctUntilChanged(),
				skipWhile<number | null>((v) => !v && !this.initialValue),
				takeUntil(this.destroyed)
			)
			.subscribe(fn);
	}
}
